import Handlebars from "handlebars";
import { type ComponentPropsWithoutRef, useMemo } from "react";
import Spinner from "shared/components/common/Spinner";
import { renderTemplateUtil } from "shared/components/template/renderTemplateUtil";
import { sanitizeHtml } from "shared/components/template/sanitize-html";
import {
  type CustomRouterType,
  useCustomRouter,
} from "shared/context/CustomRouterContext";
import { useCustomSession } from "shared/context/CustomSessionContext";

type TemplateComponentProps<T> = Omit<
  ComponentPropsWithoutRef<"div">,
  "dangerouslySetInnerHtml"
> & {
  template?: string;
  templatePath?: string;
  data?: T;
  testid?: string;
};

const TemplateComponent = <T extends object>({
  template,
  templatePath,
  data,
  testid,
  ...props
}: TemplateComponentProps<T>) => {
  const router = useCustomRouter();
  const session = useCustomSession();
  const { routerPushHandleClick } = registerRouterPushTemplateUtil({
    router,
  });
  const html = useMemo(() => {
    if (!template && !templatePath) {
      return "";
    }
    return renderTemplateUtil({
      template: template ?? "",
      templatePath,
      templateData: { session, ...data },
    });
  }, [template, templatePath, data]);

  if (!data) {
    return <Spinner />;
  }
  if (!template && !templatePath) {
    return <></>;
  }

  return (
    <div
      className={props.className}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
      onClick={(e) => {
        routerPushHandleClick(e);
      }}
      {...props}
      data-testid={testid}
    />
  );
};

export default TemplateComponent;

const registerRouterPushTemplateUtil = ({
  router,
}: {
  router: CustomRouterType;
}) => {
  if (router) {
    // Register helper that creates a button with onclick handler
    Handlebars.registerHelper(
      "routerPush",
      ({
        hash,
        fn: innerFn,
      }: Handlebars.HelperOptions & {
        hash: { route: string; type?: "button" | "anchor"; testid?: string };
      }) => {
        const testid = hash.testid ? ` data-testid="${hash.testid}"` : "";
        if (hash.type === "anchor") {
          const returnString = `<a data-route="${hash.route}" class="router-push"${testid}>${innerFn(this)}</a>`;
          return new Handlebars.SafeString(returnString);
        }
        if (hash.type === "button") {
          const returnString = `<button type="button" data-route="${hash.route}" class="router-push"${testid}>${innerFn(this)}</button>`;
          return new Handlebars.SafeString(returnString);
        }
        const returnString = "Missing type for routerPush helper";
        return new Handlebars.SafeString(returnString);
      },
    );
  }
  return {
    routerPushHandleClick: (e: React.MouseEvent<HTMLDivElement>) => {
      if (router) {
        const target = e.target as HTMLElement;
        if (target.classList.contains("router-push")) {
          const route = target.getAttribute("data-route");
          if (route) {
            router.push(route);
          }
        }
      }
    },
  };
};
